import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { MessageCircle, Send, Calculator } from 'lucide-react';

const ContactSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const mailtoHref = `mailto:?subject=${encodeURIComponent('【墓じまい相談】お問い合わせ')}&body=${encodeURIComponent(
    '以下の内容をご記入のうえ送信ください。\n\nお名前：\n地域（市町村）：\n墓地の場所（霊園名・寺院名・住所など）：\nご希望の時期：\n電話番号：\nご相談内容：\n'
  )}`;

  return (
    <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-stone-800 mb-4">
            まずはご相談から
          </h2>
          <p className="text-xl text-stone-600">
            「話を聞いてみたい」だけでも大丈夫です
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Mailto CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="h-full"
          >
            <div className="bg-white rounded-xl p-8 shadow-lg border border-stone-200 text-center h-full flex flex-col justify-between">
              <div>
                <h3 className="text-2xl font-bold text-stone-800 mb-4">メールで問い合わせる</h3>
                <p className="text-stone-600 mb-6">
                  クリックすると、お使いのメールアプリが起動します。本文に表示されるテンプレートに沿ってご記入ください。
                </p>
              </div>
              <a
                href={mailtoHref}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-amber-600 hover:bg-amber-700 text-white text-lg font-bold rounded-full transition-all duration-300 shadow-xl hover:shadow-2xl w-full"
              >
                <Send className="w-5 h-5" />
                メールで相談する
              </a>
            </div>
          </motion.div>
          
          {/* LINE Contact */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="h-full"
          >
            <div className="bg-green-600 text-white rounded-xl p-8 shadow-xl h-full flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <MessageCircle className="w-8 h-8" />
                  <h3 className="text-2xl font-bold">LINEで相談</h3>
                </div>
                <p className="mb-6 text-green-50">
                  お手軽に、日常の会話のように相談したい方は、LINEからもお問い合わせいただけます。
                </p>
              </div>
              <a
                href="https://lin.ee/QGNguZ2"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full px-6 py-4 bg-white text-green-600 font-bold rounded-full hover:bg-green-50 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                友だち追加して相談
              </a>
            </div>
          </motion.div>

          {/* Calculator Link */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="h-full"
          >
            <div className="bg-emerald-600 text-white rounded-xl p-8 shadow-xl h-full flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Calculator className="w-8 h-8" />
                  <h3 className="text-2xl font-bold">費用を概算する</h3>
                </div>
                <p className="mb-6 text-emerald-50">
                  まずは概算費用を知りたい方は、こちらのツールをご利用ください。
                </p>
              </div>
              <a
                href="https://tomosibi.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full px-6 py-4 bg-white text-emerald-600 font-bold rounded-full hover:bg-emerald-50 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                墓じまい費用を今すぐ算出
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
